import React, { useRef } from "react";
import { X, Camera } from "lucide-react";

// Mappa corporea: sagoma semplificata (viewBox 200×420), vista anteriore e posteriore.
// I marker sono salvati in coordinate del viewBox → riproducibili nel PDF (SVG rasterizzato dal backend).
const W = 200, H = 420;
// Metà destra della sagoma (x ≥ 0, asse mediano a x=0); la sinistra è ottenuta per specchiatura.
const HALF = "M0 6 C 10 6, 17 9, 19 16 C 22 26, 22 40, 19 50 C 17 56, 13 61, 10 64 L 9 72 C 9 78, 12 82, 18 86 C 30 90, 44 94, 50 102 C 58 114, 60 140, 63 170 C 66 200, 70 224, 74 246 C 77 256, 74 264, 66 264 C 60 264, 58 256, 57 246 C 54 226, 50 200, 47 168 C 45 148, 43 130, 41 122 L 41 165 C 41 185, 38 205, 40 222 C 42 240, 46 262, 46 288 C 46 314, 42 336, 42 356 C 42 376, 38 392, 36 400 C 40 406, 44 410, 42 414 L 10 414 C 10 406, 12 396, 13 384 C 14 360, 12 336, 12 312 C 12 290, 8 268, 0 252 Z";

export const regionLabel = (view, x, y) => {
  const dx = x - W / 2; // <0 = lato sinistro dell'osservatore
  const ax = Math.abs(dx);
  const front = view === "front";
  // Vista anteriore: la destra del paziente è a sinistra dell'osservatore
  const side = ax < 6 ? "" : (front ? (dx < 0 ? "dx" : "sx") : (dx < 0 ? "sx" : "dx"));
  let r;
  if (y < 66) r = front ? "Volto" : "Cuoio capelluto";
  else if (y < 88) r = front ? "Collo" : "Nuca";
  else if (ax > 40 && y >= 88) r = y < 130 ? "Spalla" : y < 190 ? "Braccio" : y < 240 ? "Avambraccio" : "Mano";
  else if (y < 160) r = front ? "Torace" : "Dorso alto";
  else if (y < 205) r = front ? "Addome" : "Dorso";
  else if (y < 252) r = front ? "Regione inguinale / pube" : "Regione lombare / glutei";
  else if (y < 320) r = "Coscia";
  else if (y < 398) r = "Gamba";
  else r = "Piede";
  return side ? `${r} ${side}` : r;
};

const Silhouette = ({ view, markers, onAdd, onRemove }) => {
  const ref = useRef(null);
  const handleClick = (e) => {
    const svg = ref.current; if (!svg) return;
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    if (p.x < 0 || p.x > W || p.y < 0 || p.y > H) return;
    onAdd(view, Math.round(p.x), Math.round(p.y));
  };
  const mine = markers.filter((m) => m.view === view);
  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">{view === "front" ? "Anteriore" : "Posteriore"}</span>
      <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="w-[120px] md:w-[140px] h-auto cursor-crosshair select-none"
           onClick={handleClick} data-testid={`body-map-${view}`} role="img" aria-label={`Sagoma ${view === "front" ? "anteriore" : "posteriore"}`}>
        <g fill="#EEF2F7" stroke="#64748B" strokeWidth="1.5" strokeLinejoin="round">
          <path d={HALF} transform={`translate(${W / 2},0)`} />
          <path d={HALF} transform={`translate(${W / 2},0) scale(-1,1)`} />
        </g>
        <line x1={W / 2} y1={6} x2={W / 2} y2={252} stroke="#CBD5E1" strokeWidth="0.8" strokeDasharray="3 3" />
        {mine.map((m) => (
          <g key={m.id} onClick={(e) => { e.stopPropagation(); onRemove(m.id); }} className="cursor-pointer" data-testid={`body-map-marker-${m.n}`}>
            <circle cx={m.x} cy={m.y} r="9" fill="#DC2626" stroke="#fff" strokeWidth="1.5" />
            <text x={m.x} y={m.y + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff" fontFamily="Helvetica, Arial, sans-serif">{m.n}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const renumber = (list) => list.map((m, i) => ({ ...m, n: i + 1 }));

export const BodyMap = ({ value = [], onChange, photoCountByMarker = {} }) => {
  const add = (view, x, y) => onChange(renumber([...value, { id: `${Date.now()}${Math.floor(Math.random() * 1000)}`, view, x, y, label: regionLabel(view, x, y) }]));
  const remove = (id) => onChange(renumber(value.filter((m) => m.id !== id)));
  const setLabel = (id, label) => onChange(value.map((m) => (m.id === id ? { ...m, label } : m)));
  return (
    <div className="rounded-lg border border-slate-300 bg-white p-3" data-testid="body-map-panel">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex gap-4 justify-center">
          <Silhouette view="front" markers={value} onAdd={add} onRemove={remove} />
          <Silhouette view="back" markers={value} onAdd={add} onRemove={remove} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-slate-500 mb-2">Clicca sulla sagoma per segnare la sede di una lesione (clic sul numero per rimuoverla). La sede viene riconosciuta automaticamente e puoi correggerla.</p>
          {value.length === 0 ? (
            <p className="text-xs text-slate-400 italic" data-testid="body-map-empty">Nessuna sede segnata.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="body-map-list">
              {value.map((m) => (
                <li key={m.id} className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{m.n}</span>
                  <input value={m.label} onChange={(e) => setLabel(m.id, e.target.value)} data-testid={`body-map-label-${m.n}`}
                         className="flex-1 h-7 rounded border border-slate-300 px-2 text-xs text-slate-800" />
                  {photoCountByMarker[m.n] > 0 && (
                    <span className="text-[10px] text-blue-900 flex items-center gap-0.5 flex-shrink-0" title="Foto collegate" data-testid={`body-map-photos-${m.n}`}><Camera className="h-3 w-3" /> {photoCountByMarker[m.n]}</span>
                  )}
                  <span className="text-[10px] text-slate-400 w-14">{m.view === "front" ? "anteriore" : "posteriore"}</span>
                  <button type="button" onClick={() => remove(m.id)} className="text-slate-400 hover:text-red-600" data-testid={`body-map-remove-${m.n}`} aria-label="Rimuovi"><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export const bodyMapSitesText = (markers = []) =>
  markers.length ? `Sedi segnate sulla mappa corporea: ${markers.map((m) => `${m.n}. ${(m.label || "").trim() || "sede"}`).join("; ")}.` : "";

// SVG autonomo (anteriore + posteriore affiancate) rasterizzato dal backend nel PDF
export const bodyMapToSvg = (markers = []) => {
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const view = (v, ox) => {
    const mine = markers.filter((m) => m.view === v);
    return `<g transform="translate(${ox},18)">
<text x="${W / 2}" y="-6" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="11" font-weight="700" fill="#475569">${v === "front" ? "ANTERIORE" : "POSTERIORE"}</text>
<g fill="#EEF2F7" stroke="#64748B" stroke-width="1.5" stroke-linejoin="round"><path d="${HALF}" transform="translate(${W / 2},0)"/><path d="${HALF}" transform="translate(${W / 2},0) scale(-1,1)"/></g>
${mine.map((m) => `<circle cx="${m.x}" cy="${m.y}" r="9" fill="#DC2626" stroke="#fff" stroke-width="1.5"/><text x="${m.x}" y="${m.y + 3.5}" text-anchor="middle" font-family="Helvetica, Arial, sans-serif" font-size="10" font-weight="700" fill="#fff">${esc(m.n)}</text>`).join("")}
</g>`;
  };
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W * 2 + 40}" height="${H + 30}" viewBox="0 0 ${W * 2 + 40} ${H + 30}"><rect width="100%" height="100%" fill="#ffffff"/>${view("front", 10)}${view("back", W + 30)}</svg>`;
};
